import * as THREE from 'three';
import { ballRadius } from '../sim/world.js';

/**
 * カット角ガイド（自前・GPL 非依存）:
 *   狙っている的玉とポケット狙点を対角頂点とする直角四辺形を、クロス直上に描く。
 *   1 辺の方向は **手玉 → 的玉中心ライン**（軸 u）、もう 1 辺はそれに直交する n。
 *   ポケットの位置を軸 u と直交 n に射影し、a = (P−T)·u、b = (P−T)·n で長方形を構築する。
 *   a と b の大きさが等しいとき正方形になり、これはカット角 45° に対応する
 *   （カット角 θ：手玉→的玉ラインと 的玉→ポケットラインのなす角）。
 *   厚いショット（θ 小）ほど扁平・薄いショット（θ 大）ほど縦長になり、目算しやすい。
 *
 * `update(vertices)` で 4 頂点（時計または反時計で閉じる順序）を更新、`hide()` で非表示。
 * 色は濃い赤・半透明・クロス直上（renderOrder=2）。
 */

export type Vec2 = { readonly x: number; readonly y: number };

export interface CutAngleFrameHandle {
  update(vertices: ReadonlyArray<Vec2>): void;
  hide(): void;
  dispose(): void;
}

const FRAME_COLOR = 0xff2233; // 濃い赤（緑/グレーのラシャ上で明瞭）
const FRAME_OPACITY = 0.95;
const FRAME_Z = -ballRadius + 0.0012; // クロス直上（影 -ballRadius+0.002 より少しだけ低く）

export const createCutAngleFrame = (scene: THREE.Scene): CutAngleFrameHandle => {
  const geom = new THREE.BufferGeometry();
  // LineSegments で 4 辺 (V0-V1, V1-V2, V2-V3, V3-V0) を明示。LineLoop / Line + 頂点複写ともに
  // 1 辺が欠ける現象を観測したため、各辺の両端点を独立に持つ LineSegments で確実に描く。
  // 8 頂点 × 3 = 24 floats。
  const positions = new Float32Array(8 * 3);
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const mat = new THREE.LineBasicMaterial({
    color: FRAME_COLOR,
    transparent: true,
    opacity: FRAME_OPACITY,
    depthWrite: false,
    // コーナーポケット付近では他メッシュ（レール内縁・ポケット黒穴）に辺が隠れることがあるため、
    // デプステストを無効化し常に手前に描画する（renderOrder=2 と併用）。
    depthTest: false,
  });
  const segments = new THREE.LineSegments(geom, mat);
  segments.position.z = FRAME_Z;
  segments.visible = false;
  segments.renderOrder = 2;
  // BufferGeometry の boundingSphere は初回計算後に固定されるため、頂点位置を毎フレーム更新しても
  // 反映されない。結果、特定のカメラズーム/角度でフラスタムカリングが「メッシュは画面外」と誤判定
  // して描画をスキップすることがある（実機で観測）。本ガイドは線4本のみで負荷ゼロに近いため、
  // フラスタムカリングを無効化して常に描画対象に含める。
  segments.frustumCulled = false;
  scene.add(segments);

  const writePair = (segIndex: number, a: Vec2, b: Vec2): void => {
    const o = segIndex * 6; // 2 頂点 × 3 floats
    positions[o] = a.x;
    positions[o + 1] = a.y;
    positions[o + 2] = 0;
    positions[o + 3] = b.x;
    positions[o + 4] = b.y;
    positions[o + 5] = 0;
  };

  const update = (vertices: ReadonlyArray<Vec2>): void => {
    const v0 = vertices[0] ?? { x: 0, y: 0 };
    const v1 = vertices[1] ?? { x: 0, y: 0 };
    const v2 = vertices[2] ?? { x: 0, y: 0 };
    const v3 = vertices[3] ?? { x: 0, y: 0 };
    writePair(0, v0, v1);
    writePair(1, v1, v2);
    writePair(2, v2, v3);
    writePair(3, v3, v0); // 4 辺目 = 閉じる辺を明示
    geom.attributes.position!.needsUpdate = true;
    segments.visible = true;
  };

  const hide = (): void => {
    segments.visible = false;
  };

  const dispose = (): void => {
    scene.remove(segments);
    geom.dispose();
    mat.dispose();
  };

  return { update, hide, dispose };
};
